import { useAuthStore } from "@/store/useAuthStore";
import { usePrivateStore } from "@/store/usePrivateStore";
import { message, Select } from "antd";
import { useMemo, useEffect, useCallback, useState } from "react";
import { useLocalFilter } from "@/hooks/useOrganizationIndexedDBFilter";
import useOrganizationCache from "@/hooks/useOrganizationIndexedDB";
import { useTranslation } from "react-i18next";
import { useSweetAlert } from "../../hooks/useSweetAlert";
import {
    getKiemNhiemChilds,
    resolveDefaultKiemNhiemId,
    switchToKiemNhiem,
    syncKiemNhiemBpid,
    type KiemNhiemChild,
} from '@/utils/kiemNhiemUtils';
import { useRouter } from 'next/router';
import { resolvePhanHeFromRoute } from '@/utils/phanHeStorage';
import { resolveEffectivePathname } from '@/components/PhanHe/VanHoaPhanHeHandler';
import { shouldUseVanHoaBscStyleHeader } from '@/configs/ROUTE_HEADER';
import { CONSTANTS } from '@/utils/constants';

const { Option } = Select;

function buildDeptNameMap(departments: unknown[]): Record<string, string> {
    const map: Record<string, string> = {};
    for (const item of departments) {
        if (!item || typeof item !== 'object') continue;
        const dept = item as Record<string, unknown>;
        const id = String(dept.id ?? dept.ID ?? '').trim();
        const name = String(dept.ten ?? dept.Ten ?? dept.tenBoPhan ?? dept.TenBoPhan ?? '').trim();
        if (id && name) map[id] = name;
    }
    return map;
}

export const DropDownKiemNhiem: React.FC = () => {
    const router = useRouter();
    const { profile, setAuthToken, setIsAuth, setProfile, setEmail } = useAuthStore();
    const storedKiemNhiemId = usePrivateStore((s) => s.appHeader.kiemNhiemBpid);
    const { setAppHeader, clearTreeSelectDepartment } = usePrivateStore();
    const { loadDepartmentData } = useLocalFilter({ enabled: true });
    const { getDepartments } = useOrganizationCache();
    const sweetAlert = useSweetAlert();
    const { t } = useTranslation();

    const childs = useMemo(() => getKiemNhiemChilds(profile), [profile]);

    const selectedValue = useMemo(
        () => resolveDefaultKiemNhiemId(profile, storedKiemNhiemId),
        [profile, storedKiemNhiemId],
    );

    useEffect(() => {
        if (childs.length <= 1) return;
        syncKiemNhiemBpid(profile, storedKiemNhiemId);
    }, [childs.length, profile, storedKiemNhiemId]);

    const resolveChildLabel = useCallback(
        (child: KiemNhiemChild, deptNameById: Record<string, string>, duplicateBpid: boolean) => {
            const fallback = t('bsc.label.chuacoten', 'Chưa có tên');
            let label = child.tenBoPhan || deptNameById[child.bpid] || '';

            if (!label && child.bpid) {
                const fromScoped = loadDepartmentData(child.bpid, 'ten');
                if (fromScoped) label = String(fromScoped);
            }

            if (!label) label = fallback;
            if (duplicateBpid && child.tenChucVu) {
                label = `${label} (${child.tenChucVu})`;
            }
            return label;
        },
        [loadDepartmentData, t],
    );

    const [deptNameById, setDeptNameById] = useState<Record<string, string>>({});

    useEffect(() => {
        void getDepartments()
            .then((departments) => {
                const list = Array.isArray(departments) ? departments : Object.values(departments || {});
                setDeptNameById(buildDeptNameMap(list));
            })
            .catch(() => setDeptNameById({}));

        const onReload = () => {
            void getDepartments()
                .then((departments) => {
                    const list = Array.isArray(departments) ? departments : Object.values(departments || {});
                    setDeptNameById(buildDeptNameMap(list));
                })
                .catch(() => setDeptNameById({}));
        };

        window.addEventListener('indexedDBReloaded', onReload);
        return () => window.removeEventListener('indexedDBReloaded', onReload);
    }, [getDepartments]);

    const options = useMemo(() => {
        const bpidCounts = childs.reduce<Record<string, number>>((acc, child) => {
            if (!child.bpid) return acc;
            acc[child.bpid] = (acc[child.bpid] ?? 0) + 1;
            return acc;
        }, {});

        return childs.map((child) => ({
            id: child.id,
            label: resolveChildLabel(child, deptNameById, (bpidCounts[child.bpid] ?? 0) > 1),
            child,
        }));
    }, [childs, deptNameById, resolveChildLabel]);

    const handleChange = async (value: string) => {
        if (value === selectedValue) return;

        const hideLoading = message.loading(t('api.switch.kiemnhiem.progress', 'Đang kiểm tra thông tin kiêm nhiệm!'));
        const result = await switchToKiemNhiem(value);
        hideLoading();

        if (!result.ok) {
            await sweetAlert.showError(
                t(
                    'api.error.khongthelaythongtinnhanvienkiemnhiem',
                    'Lấy thông tin nhân viên không thành công! Vui lòng kiểm tra lại thông tin kiêm nhiệm.'
                )
            );
            return;
        }

        setAuthToken(result.loginResponse);
        setEmail(result.email);
        setIsAuth(true);
        setProfile(result.profile);
        setAppHeader({ kiemNhiemBpid: value });
        clearTreeSelectDepartment();
        document.location.reload();
    };

    const isVanHoaPhanHe =
        resolvePhanHeFromRoute(router.pathname, router.asPath) === CONSTANTS.PHAN_HE.VAN_HOA;
    const effectivePathname = resolveEffectivePathname(router.pathname, router.asPath);
    const useVanHoaBscStyleHeader = shouldUseVanHoaBscStyleHeader(effectivePathname);

    if ((isVanHoaPhanHe && !useVanHoaBscStyleHeader) || childs.length <= 1) {
        return null;
    }

    return (
        <div className="flex items-center gap-x-2">
            <span className="text-sm text-[#595959] whitespace-nowrap">
                {t('bsc.label.kiemnhiem', 'Kiêm nhiệm:')}
            </span>
            <Select
                style={{ minWidth: '150px' }}
                placeholder={t('bsc.placeholder.chonphongbankiemnhiem', 'Chọn phòng ban kiêm nhiệm')}
                value={selectedValue}
                onChange={handleChange}
            >
                {options.map((option, index) => (
                    <Option key={option.child.id || index} value={option.id}>
                        {option.label}
                    </Option>
                ))}
            </Select>
        </div>
    );
};
